import { Link } from 'react-router-dom';
import LegalPage from '../components/LegalPage';
import { LEGAL_ENTITY_LONG, SUPPORT_EMAIL } from '../legalInfo';

const SECTIONS = [
  {
    id: 'scope',
    heading: 'Scope',
    body: (
      <>
        <p>
          This Privacy Policy explains what personal information {LEGAL_ENTITY_LONG}{' '}
          (&ldquo;Mermadic&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;) collects when you visit our
          website or use the Mermadic dashboard and APIs, how we use it, and the choices you
          have.
        </p>
        <p>
          It does not govern the content you choose to run on the platform. You control that
          &mdash; see <a href="#workload-content">Your workload content</a>.
        </p>
        <p>
          Mermadic is a business product intended for adults. We do not knowingly collect
          information from anyone under 18.
        </p>
      </>
    ),
  },
  {
    id: 'what-we-collect',
    heading: 'Information we collect',
    body: (
      <>
        <ul>
          <li>
            <strong>Account information</strong> &mdash; the name, email address, and credentials
            you provide at signup. Passwords are stored hashed and are never visible to us in
            plain text.
          </li>
          <li>
            <strong>Billing information</strong> &mdash; Stripe processes your card. We receive
            and store limited details such as card brand, last four digits, expiry, and your
            invoice and payment history. We never store full card numbers.
          </li>
          <li>
            <strong>Usage and metering data</strong> &mdash; jobs, cron schedules, deployments,
            run times, CPU, memory and GPU consumption, and the charges that result.
          </li>
          <li>
            <strong>Workload metadata</strong> &mdash; names of your clusters, jobs, deployments,
            ConfigMaps, Secrets, and images, plus the job and build logs we store so you can view
            them.
          </li>
          <li>
            <strong>Technical data</strong> &mdash; IP address, browser and device type, pages
            viewed, timestamps, and diagnostic or error logs.
          </li>
          <li>
            <strong>Integration data</strong> &mdash; if you connect GitHub, the repositories and
            metadata the GitHub App is authorized to see.
          </li>
          <li>
            <strong>Support messages</strong> &mdash; what you send us through the contact form or
            by email.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'how-we-use',
    heading: 'How we use it',
    body: (
      <>
        <ul>
          <li>Operate, secure, and troubleshoot the platform.</li>
          <li>Authenticate you and your API keys.</li>
          <li>Meter usage, bill you, and collect payment.</li>
          <li>
            Send transactional email such as verification codes, password resets, billing
            notices, and incident notifications. These are part of running your account, so you
            cannot opt out of them while the account is open.
          </li>
          <li>Provide support when you contact us.</li>
          <li>Detect abuse, fraud, cryptocurrency mining, and security threats.</li>
          <li>Understand how the site is used so we can improve it.</li>
          <li>
            Send marketing email about Mermadic, only if you opted in. Every marketing email
            includes an unsubscribe link.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'legal-bases',
    heading: 'Our legal bases for using it',
    body: (
      <>
        <p>
          If you are in the UK or the European Economic Area, data protection law requires us to
          have a legal basis for each use. Ours are:
        </p>
        <table className="legal-table">
          <thead>
            <tr>
              <th>Basis</th>
              <th>What we rely on it for</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Performance of a contract</td>
              <td>
                Running your account and workloads, metering usage, billing you, and sending
                transactional email
              </td>
            </tr>
            <tr>
              <td>Legitimate interests</td>
              <td>
                Securing the platform, preventing abuse and fraud, and improving the product
              </td>
            </tr>
            <tr>
              <td>Consent</td>
              <td>Marketing email and advertising cookies, which you can withdraw at any time</td>
            </tr>
            <tr>
              <td>Legal obligation</td>
              <td>Keeping tax and accounting records and responding to lawful requests</td>
            </tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'workload-content',
    heading: 'Your workload content',
    body: (
      <>
        <p>
          Your container images, ConfigMaps, Secrets, environment data, and job logs belong to
          you. We access them only to run the platform, to keep it secure, or when you ask us to
          help with a support issue. We do not sell your workload content, use it for advertising,
          or use it to train models.
        </p>
        <p>
          Secrets are held differently from the rest of your workload content: values are
          encrypted before we store them, are decrypted only to start the workload you attached
          them to, and are never returned by the dashboard or the API. That applies to our support
          team as well &mdash; we cannot look up a Secret&rsquo;s value for you, so a forgotten one
          has to be replaced rather than recovered.
        </p>
        <div className="legal-callout">
          <p>
            Put credentials in a Secret rather than a ConfigMap &mdash; ConfigMap values stay
            readable. Either way, avoid putting sensitive values into job names or log output,
            where they are much easier to expose.
          </p>
        </div>
      </>
    ),
  },
  {
    id: 'cookies',
    heading: 'Cookies and tracking',
    body: (
      <>
        <p>
          <strong>Essential storage.</strong> We use cookies and browser storage to keep you
          signed in and remember your selected cluster. The dashboard does not work without them.
        </p>
        <p>
          <strong>Advertising measurement.</strong> We use Google Ads conversion tracking to
          measure which ads lead to signups. This sets Google cookies and shares limited event
          data, such as the fact that a signup occurred, with Google. It never includes your
          workload data or job logs.
        </p>
        <p>
          You can block cookies in your browser, though the dashboard may stop working correctly.
          You can also manage ad personalization in your Google account settings.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    heading: 'Who we share it with',
    body: (
      <>
        <p>
          <strong>We do not sell your personal information.</strong> We share it with service
          providers who help us run Mermadic:
        </p>
        <table className="legal-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Amazon Web Services</td>
              <td>Hosting, compute, storage, and transactional email delivery</td>
            </tr>
            <tr>
              <td>Stripe</td>
              <td>Payment processing and card storage</td>
            </tr>
            <tr>
              <td>Google</td>
              <td>Advertising conversion measurement</td>
            </tr>
            <tr>
              <td>GitHub</td>
              <td>Source-code integration that you explicitly connect</td>
            </tr>
          </tbody>
        </table>
        <p>
          We may also disclose information when required by law, to enforce our{' '}
          <Link to="/terms">Terms of Service</Link>, to protect rights and safety, or in
          connection with a merger or acquisition. If ownership changes, we will notify you.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    heading: 'How long we keep it',
    body: (
      <>
        <ul>
          <li>
            <strong>Account and billing records</strong> are kept while your account is open, and
            afterwards for as long as we need them for tax, accounting, and legal purposes.
          </li>
          <li>
            <strong>Job logs, build logs, and workload metadata</strong> are kept while the
            related cluster and account are active, and are deleted when the cluster or account
            is deleted.
          </li>
          <li>
            Operational backups age out on their own schedule after deletion.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'security',
    heading: 'Security',
    body: (
      <>
        <p>
          Traffic is encrypted in transit with TLS. Passwords are hashed, and API keys are stored
          so that they cannot be read back. Dedicated clusters run in their own isolated network.
          Access to production systems is limited to staff who need it.
        </p>
        <p>
          No system is perfectly secure and we do not claim any formal compliance certification.
          If we become aware of a breach affecting your personal information, we will notify you
          as required by law.
        </p>
      </>
    ),
  },
  {
    id: 'your-rights',
    heading: 'Your rights and choices',
    body: (
      <>
        <p>
          Depending on where you live, you may have the right to access, correct, delete, or
          receive a copy of your personal information, to object to or restrict how we process
          it, and not to be treated differently for exercising those rights.
        </p>
        <p>
          You can update most details yourself in your account settings, rotate or revoke API
          keys, and delete clusters and images at any time. For anything else, email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We verify requests through your
          account email address and respond within 30 days.
        </p>
        <p>
          Where we rely on your consent &mdash; for marketing email or advertising cookies &mdash;
          you can withdraw it at any time. Withdrawing consent does not affect anything we did
          before you withdrew it.
        </p>
        <p>
          We do not sell personal information, and we do not share it for cross-context
          behavioral advertising as that term is defined under California law.
        </p>
        <p>
          If you are in the UK or the European Economic Area and you think we are handling your
          information unlawfully, you can complain to your national data protection authority.
          We would appreciate the chance to address it first.
        </p>
      </>
    ),
  },
  {
    id: 'international',
    heading: 'International users',
    body: (
      <>
        <p>
          Our infrastructure is located in the United States. If you use Mermadic from elsewhere,
          your information is transferred to and processed in the United States.
        </p>
      </>
    ),
  },
  {
    id: 'restricted-data',
    heading: 'Restricted data',
    body: (
      <>
        <p>
          Do not upload protected health information, cardholder data, or other specially
          regulated personal data to the platform. See the restricted data section of our{' '}
          <Link to="/terms">Terms of Service</Link>.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: 'Changes to this policy',
    body: (
      <>
        <p>
          We will post updates on this page with a new last-updated date. For material changes we
          will also notify you by email or in-product.
        </p>
      </>
    ),
  },
];

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      subtitle="What we collect when you use Mermadic, why we collect it, and how you stay in control of it."
      sections={SECTIONS}
    />
  );
}
